import React, { useEffect, useState, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { Cpu, Activity, TrendingUp, Loader2 } from 'lucide-react';
import { cn } from '../lib/utils';

const fmtMoney = (n) => `$${(n || 0).toFixed(4)}`;
const fmtTime = (s) => {
  if (!s) return '';
  try { return new Date(s).toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit', second: '2-digit' }); }
  catch { return s; }
};

/**
 * Phase 8 — Model Routing panel.
 * Shows the model registry, the resolved use-case defaults, the user's
 * 24h cost summary, and recent model calls with provenance (resolved_via).
 */
export default function ModelRoutingPanel() {
  const [models, setModels] = useState([]);
  const [summary, setSummary] = useState(null);
  const [recent, setRecent] = useState([]);
  const [loading, setLoading] = useState(false);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const [mr, or] = await Promise.all([
        fetch('/api/ai/v2/routing/models', { credentials: 'include' }),
        fetch('/api/ai/v2/observability/model-calls?window_hours=24&limit=20', { credentials: 'include' }),
      ]);
      if (mr.ok) {
        const d = await mr.json();
        setModels(d.models || []);
      }
      if (or.ok) {
        const d = await or.json();
        setSummary(d.summary || null);
        setRecent(d.recent || []);
      }
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const TIER_COLOR = {
    small:     'bg-emerald-500/10 text-emerald-600 border-emerald-500/30',
    medium:    'bg-amber-500/10 text-amber-600 border-amber-500/30',
    large:     'bg-violet-500/10 text-violet-600 border-violet-500/30',
    xlarge:    'bg-fuchsia-500/10 text-fuchsia-600 border-fuchsia-500/30',
    embedding: 'bg-slate-500/10 text-slate-600 border-slate-500/30',
  };

  return (
    <Card className="h-full flex flex-col" data-testid="model-routing-panel">
      <CardHeader className="py-4 px-6 border-b">
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="flex items-center gap-2"><Cpu className="w-5 h-5 text-violet-500" />Model Routing</CardTitle>
            <CardDescription>Per-call model resolution + cost observability</CardDescription>
          </div>
          <Button size="sm" variant="outline" onClick={refresh} disabled={loading} data-testid="model-routing-refresh">
            {loading && <Loader2 className="w-4 h-4 mr-1 animate-spin" />} Refresh
          </Button>
        </div>
      </CardHeader>

      <CardContent className="p-0 flex-1 overflow-hidden">
        <div className="grid grid-cols-3 h-full">
          {/* Models registry */}
          <div className="border-r p-4 overflow-y-auto" data-testid="model-routing-models">
            <h4 className="text-xs uppercase tracking-wide text-muted-foreground mb-2">Available Models</h4>
            <div className="space-y-2">
              {models.map(m => (
                <div key={m.model_id} className="rounded-lg border bg-card p-3 text-sm">
                  <div className="flex items-center gap-2">
                    <span className="font-mono text-xs">{m.model_id}</span>
                    <Badge variant="outline" className={cn('text-[10px]', TIER_COLOR[m.tier])}>{m.tier}</Badge>
                    <Badge variant="secondary" className="text-[10px]">{m.provider}</Badge>
                  </div>
                  <p className="text-xs text-muted-foreground mt-1 line-clamp-2">{m.description}</p>
                  <div className="text-[10px] mt-1 text-muted-foreground">
                    in: ${m.cost_input_per_1k}/1k · out: ${m.cost_output_per_1k}/1k
                  </div>
                  {m.fallback_model_id && (
                    <div className="text-[10px] mt-0.5 text-muted-foreground">
                      fallback → <span className="font-mono">{m.fallback_model_id}</span>
                    </div>
                  )}
                </div>
              ))}
              {models.length === 0 && !loading && (
                <div className="text-xs text-muted-foreground p-3">No models registered.</div>
              )}
            </div>
          </div>

          {/* Summary + Recent calls */}
          <div className="col-span-2 flex flex-col overflow-hidden">
            {/* Summary tiles */}
            <div className="grid grid-cols-3 gap-3 p-4 border-b">
              <SummaryTile icon={Activity} label="Calls (24h)" value={summary?.total_calls ?? 0} />
              <SummaryTile icon={TrendingUp} label="Tokens (in+out)"
                value={`${(summary?.total_input_tokens ?? 0).toLocaleString()} / ${(summary?.total_output_tokens ?? 0).toLocaleString()}`} />
              <SummaryTile icon={Cpu} label="Cost (24h)" value={fmtMoney(summary?.total_cost_usd ?? 0)}
                tone={summary?.total_failures > 0 ? 'amber' : undefined}
                hint={summary?.total_failures > 0 ? `${summary.total_failures} failure${summary.total_failures === 1 ? '' : 's'}` : undefined} />
            </div>

            {/* Per-model breakdown */}
            <div className="px-4 py-2 border-b">
              <h4 className="text-xs uppercase tracking-wide text-muted-foreground mb-2">By Model (24h)</h4>
              {summary?.by_model?.length ? (
                <div className="grid grid-cols-2 gap-2">
                  {summary.by_model.map(b => (
                    <div key={b.model_id} className="rounded border bg-card px-3 py-2 text-xs flex items-center gap-2">
                      <span className="font-mono">{b.model_id}</span>
                      <span className="ml-auto text-muted-foreground">{b.calls} call{b.calls === 1 ? '' : 's'}</span>
                      <span className="text-muted-foreground">·</span>
                      <span>{fmtMoney(b.cost_usd)}</span>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-xs text-muted-foreground py-2">No calls yet.</div>
              )}
            </div>

            {/* Recent calls */}
            <div className="flex-1 overflow-y-auto p-4">
              <h4 className="text-xs uppercase tracking-wide text-muted-foreground mb-2">Recent Calls</h4>
              {recent.length === 0 && (
                <div className="text-xs text-muted-foreground">No recent calls.</div>
              )}
              <div className="space-y-1">
                {recent.map(c => (
                  <div key={c.call_id}
                    className={cn('flex items-center gap-2 text-xs rounded px-2 py-1.5',
                      c.success ? 'bg-card' : 'bg-red-500/5 border border-red-500/20')}
                    data-testid={`model-call-${c.call_id}`}>
                    <span className="text-muted-foreground">{fmtTime(c.created_at)}</span>
                    <span className="font-mono">{c.model_id}</span>
                    <Badge variant="secondary" className="text-[10px]">{c.use_case}</Badge>
                    <Badge variant="outline" className="text-[10px]">{c.resolved_via}</Badge>
                    <span className="ml-auto text-muted-foreground">
                      {c.input_tokens}/{c.output_tokens} tok
                    </span>
                    <span>{fmtMoney(c.cost_usd)}</span>
                    <span className="text-muted-foreground">{c.duration_ms}ms</span>
                    {!c.success && <span className="text-red-500">{c.error_code}</span>}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

function SummaryTile({ icon: Icon, label, value, hint, tone }) {
  return (
    <div className={cn('rounded-lg border bg-card px-3 py-2',
      tone === 'amber' && 'border-amber-500/40 bg-amber-500/5')}>
      <div className="flex items-center gap-2 text-[10px] uppercase tracking-wide text-muted-foreground">
        <Icon className="w-3 h-3" /> {label}
      </div>
      <div className="text-lg font-semibold mt-0.5">{value}</div>
      {hint && <div className="text-[10px] text-amber-600">{hint}</div>}
    </div>
  );
}
